'use client';

import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import { TrustpilotMicroFacade } from '@/components/ui/trustpilot-micro-facade';
import { siteChrome } from '@/lib/site-chrome';
import { isTrustboxImpression, microFacadeCopy, TRUSTBOX_PAINT_SETTLE_MS } from '@/lib/trustpilot-facade';
import { useDeferredLoad } from '@/lib/use-deferred-load';

const { trustpilot } = siteChrome.brand;
const MICRO_FACADE_COPY = microFacadeCopy(trustpilot);

const TRUSTPILOT_BOOTSTRAP_URL =
  'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

/** The heights the TrustBox settles at, reserved up front so nothing shifts. */
const HEIGHTS = { micro: 20, carousel: 140 } as const;

declare global {
  interface Window {
    Trustpilot?: { loadFromElement: (el: HTMLElement, refresh?: boolean) => void };
  }
}

interface TrustpilotWidgetProps {
  variant: 'micro' | 'carousel';
  theme: 'light' | 'dark';
  className?: string;
}

/**
 * Renders a real Trustpilot TrustBox. Widget ids extracted from the live
 * site's embeds; the bootstrap script is loaded once and re-scans each
 * mounted widget so it works after Next.js hydration.
 *
 * The bootstrap is deferred until the box is near the viewport and the main
 * thread is idle (useDeferredLoad). It appears on every page in both the
 * header and the footer, so it was loading vendor JS during first paint on
 * every single navigation for a rating nobody reads in the first second. The
 * markup still renders server-side, at the height the TrustBox will occupy,
 * so deferring costs no layout shift.
 *
 * Until then the micro TrustBox is stood in for by TrustpilotMicroFacade, a
 * static copy of the widget ("Excellent 5.0 out of 5 ★ Trustpilot"). The real
 * iframe loads hidden underneath and takes over a beat after it posts its
 * impression (TRUSTBOX_PAINT_SETTLE_MS), so the swap shows no loader and no
 * blank frame. A keyboard user
 * sitting on the facade's link keeps it until they move on. The carousel
 * (footer only) keeps Trustpilot's plain fallback link.
 */
export function TrustpilotWidget({ variant, theme, className }: TrustpilotWidgetProps) {
  const widgetRef = useRef<HTMLDivElement>(null);
  const [containerRef, ready] = useDeferredLoad<HTMLDivElement>();
  const widgetKey = `${variant}-${theme}`;
  // Which widget (by key) has rendered, so a theme swap brings the facade back.
  const [renderedKey, setRenderedKey] = useState<string>();
  const [facadeFocused, setFacadeFocused] = useState(false);
  const isMicro = variant === 'micro';
  const showFacade = isMicro && (renderedKey !== widgetKey || facadeFocused);

  useEffect(() => {
    if (!ready || !isMicro) return undefined;
    let settle: number | undefined;
    const onMessage = (event: MessageEvent) => {
      const frame = widgetRef.current?.querySelector('iframe')?.contentWindow;
      if (!isTrustboxImpression(event, frame)) return;
      window.clearTimeout(settle);
      settle = window.setTimeout(() => setRenderedKey(widgetKey), TRUSTBOX_PAINT_SETTLE_MS);
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(settle);
    };
  }, [ready, isMicro, widgetKey]);

  // Re-run whenever the theme changes: the TrustBox renders into an iframe
  // once, so a theme swap needs an explicit re-render or the old (dark) text
  // stays on the new background.
  useEffect(() => {
    if (ready && widgetRef.current && window.Trustpilot) {
      window.Trustpilot.loadFromElement(widgetRef.current, true);
    }
  }, [ready, theme, variant]);

  return (
    <div className={`relative ${className ?? ''}`} ref={containerRef}>
      {ready ? (
        <Script
          src={TRUSTPILOT_BOOTSTRAP_URL}
          strategy="afterInteractive"
          onLoad={() => {
            if (widgetRef.current && window.Trustpilot) {
              window.Trustpilot.loadFromElement(widgetRef.current, true);
            }
          }}
        />
      ) : null}
      <div
        key={widgetKey}
        ref={widgetRef}
        className="trustpilot-widget"
        style={{ minHeight: HEIGHTS[variant], visibility: showFacade ? 'hidden' : undefined }}
        data-locale="en-US"
        data-template-id={isMicro ? trustpilot.microTemplateId : trustpilot.carouselTemplateId}
        data-businessunit-id={trustpilot.businessUnitId}
        data-style-height={`${HEIGHTS[variant]}px`}
        data-style-width="100%"
        data-theme={theme}
        {...(isMicro ? { 'data-token': trustpilot.microToken } : { 'data-stars': '4,5' })}
      >
        {isMicro ? null : (
          <a href={trustpilot.reviewsUrl} target="_blank" rel="noreferrer noopener">
            Trustpilot
          </a>
        )}
      </div>
      {showFacade ? (
        <TrustpilotMicroFacade copy={MICRO_FACADE_COPY} theme={theme} onFocusChange={setFacadeFocused} />
      ) : null}
    </div>
  );
}
